import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useAuth } from '@/auth/AuthContext'
import { useRunOnlyRedirect } from '@/auth/useRunOnlyRedirect'
import {
  listDeployableObjects,
  preDeployCheck,
  executeDeploy,
  deploySelected,
  rollbackDeployment,
  getDeploymentHistory,
  dbStructurePreview,
  getUncommittedChanges,
} from '@/api/deploy'
import type { UncommittedChange } from '@/api/deploy'
import { getProject, scmFetch, scmPull } from '@/api/workbench'
import api from '@/api/client'
import DeploymentProfilesPanel from './DeploymentProfilesPanel'
import type {
  DeploymentProfile,
  DeploymentHistoryItem,
  PreDeployCheckResult,
  DeployableObject,
  DbStructurePreview,
  TableStructureChange,
} from '@/types'

const STATUS_COLORS: Record<string, string> = {
  completed: '#22c55e',
  // Not a deployment: the baseline an import records for an object that came
  // OFF the app server and is therefore already deployed on it. Slate, so it
  // reads as a record rather than as a run that succeeded.
  imported: '#64748b',
  failed: '#ef4444',
  rolled_back: '#f97316',
  in_progress: '#3b82f6',
  pending: '#6b7280',
  deploying_db: '#3b82f6',
  generating_xml: '#8b5cf6',
  deploying_xml: '#06b6d4',
}

function StatusBadge({ status }: { status: string }) {
  const color = STATUS_COLORS[status] || '#6b7280'
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 12,
        fontSize: 13,
        fontWeight: 600,
        color: '#fff',
        background: color,
        textTransform: 'capitalize',
      }}
    >
      {status.replace(/_/g, ' ')}
    </span>
  )
}

/**
 * The four states an object can be in, in the order they are decided:
 *
 *   New       — no baseline at all: created here and never deployed.
 *   Changed   — edited since whichever baseline it has.
 *   Imported  — it came off the app server with the project and has not been
 *               deployed from here since, so the server already has it.
 *   Up to date — deployed from here, unchanged since.
 */
function ChangeStatusBadge({ obj }: { obj: DeployableObject }) {
  if (obj.never_deployed) {
    return (
      <span
        style={{
          display: 'inline-block',
          padding: '2px 8px',
          borderRadius: 10,
          fontSize: 13,
          fontWeight: 600,
          color: '#fff',
          background: '#3b82f6',
        }}
      >
        New
      </span>
    )
  }
  if (obj.has_changed) {
    return (
      <span
        style={{
          display: 'inline-block',
          padding: '2px 8px',
          borderRadius: 10,
          fontSize: 13,
          fontWeight: 600,
          color: '#fff',
          background: '#f97316',
        }}
      >
        Changed
      </span>
    )
  }
  if (obj.baseline_status === 'imported') {
    return (
      <span
        style={{
          display: 'inline-block',
          padding: '2px 8px',
          borderRadius: 10,
          fontSize: 13,
          fontWeight: 600,
          color: '#fff',
          background: '#64748b',
        }}
        title="Imported from the app server with this project — not deployed from here"
      >
        Imported
      </span>
    )
  }
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 10,
        fontSize: 13,
        fontWeight: 500,
        color: 'var(--color-text-secondary)',
        background: 'var(--color-bg-secondary, rgba(128,128,128,0.1))',
      }}
    >
      Up to date
    </span>
  )
}

/** Unique key for a deployable object */
function objKey(o: { obj_type: string; obj_name: string }) {
  return `${o.obj_type}:${o.obj_name}`
}

export default function DeployPage() {
  const { project } = useParams<{ project: string }>()
  useRunOnlyRedirect(project)
  const navigate = useNavigate()
  const { user } = useAuth()
  const projectId = decodeURIComponent(project || '')

  // State
  const [profiles, setProfiles] = useState<DeploymentProfile[]>([])
  const [deployableObjects, setDeployableObjects] = useState<DeployableObject[]>([])
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const [selectedProfile, setSelectedProfile] = useState<number | ''>('')
  const [showProfiles, setShowProfiles] = useState(false)
  const [versionTag, setVersionTag] = useState('')
  // Source-control outcome of the deploy that just ran. The app server and the
  // repository are two separate destinations and either can fail on its own, so
  // a push that did not land is reported instead of being folded into the
  // deployment's overall "completed".
  const [gitNotice, setGitNotice] = useState<
    { level: 'error' | 'warn' | 'ok'; text: string } | null
  >(null)
  // A git failure carries the remote's own output, which can run to dozens of
  // lines. Show the first three and let the message be opened in place.
  const [gitNoticeExpanded, setGitNoticeExpanded] = useState(false)
  const [gitNoticeClamped, setGitNoticeClamped] = useState(false)
  const gitNoticeRef = useRef<HTMLDivElement | null>(null)
  // Source control is the deploy's second destination: deploying also commits
  // and pushes the project. When origin already carries commits this copy does
  // not have, that push buries them, so a deploy waits for the pull.
  const [syncPrompt, setSyncPrompt] = useState<{ behind: number; ahead: number | null } | null>(null)
  const [syncPulling, setSyncPulling] = useState(false)
  const syncResolveRef = useRef<((proceed: boolean) => void) | null>(null)
  const [commitMessage, setCommitMessage] = useState('')
  const [checks, setChecks] = useState<PreDeployCheckResult | null>(null)
  const [checkTarget, setCheckTarget] = useState('')
  const [checkRunning, setCheckRunning] = useState(false)
  const [deploying, setDeploying] = useState(false)
  const [deployProgress, setDeployProgress] = useState('')
  const [history, setHistory] = useState<DeploymentHistoryItem[]>([])
  const [loadingHistory, setLoadingHistory] = useState(true)
  const [projectTitle, setProjectTitle] = useState(projectId)
  const [loadingObjects, setLoadingObjects] = useState(true)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewData, setPreviewData] = useState<DbStructurePreview | null>(null)
  const [previewModalOpen, setPreviewModalOpen] = useState(false)
  const [uncommittedChanges, setUncommittedChanges] = useState<UncommittedChange[]>([])
  const [uncommittedModalOpen, setUncommittedModalOpen] = useState(false)
  const [uncommittedLoading, setUncommittedLoading] = useState(false)

  // Stores the pending deploy params so modal buttons can trigger deploy
  const [pendingDeploy, setPendingDeploy] = useState<{
    mode: 'single' | 'selected'
    obj?: DeployableObject
    selected?: { obj_name: string; obj_type: string }[]
  } | null>(null)

  const changedObjects = deployableObjects.filter((o) => o.has_changed || o.never_deployed)
  const selectedCount = selectedKeys.size

  // Profiles are also editable inline (DeploymentProfilesPanel below), so the
  // fetch is a named callback the panel can re-run after a change.
  const loadProfiles = useCallback(() => {
    api
      .get('/users/deployment-profiles')
      .then((res) => {
        const list: DeploymentProfile[] = res.data.profiles || []
        setProfiles(list)
        const active = list.find((p) => p.is_active)
        if (active) {
          setSelectedProfile((cur) => (cur === '' ? active.id : cur))
        }
      })
      .catch(() => {})
  }, [])

  // Load project info, profiles, deployable objects, and history
  useEffect(() => {
    if (!projectId) return

    getProject(projectId)
      .then((p) => setProjectTitle(p.application.title || projectId))
      .catch(() => {})

    loadProfiles()

    refreshObjects()
    loadHistory()
  }, [projectId])

  const refreshObjects = useCallback(() => {
    setLoadingObjects(true)
    listDeployableObjects(projectId)
      .then((objs) => {
        setDeployableObjects(objs)
        // Auto-select all changed/new objects
        const changed = new Set(
          objs
            .filter((o) => o.has_changed || o.never_deployed)
            .map((o) => objKey(o))
        )
        setSelectedKeys(changed)
      })
      .catch(() => {})
      .finally(() => setLoadingObjects(false))
  }, [projectId])

  const loadHistory = useCallback(() => {
    setLoadingHistory(true)
    getDeploymentHistory(projectId)
      .then(setHistory)
      .catch(() => {})
      .finally(() => setLoadingHistory(false))
  }, [projectId])

  const toggleSelect = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedKeys.size === deployableObjects.length) {
      setSelectedKeys(new Set())
    } else {
      setSelectedKeys(new Set(deployableObjects.map((o) => objKey(o))))
    }
  }

  const selectAllChanged = () => {
    setSelectedKeys(new Set(changedObjects.map((o) => objKey(o))))
  }

  const handleViewUncommittedChanges = async () => {
    setUncommittedLoading(true)
    setUncommittedModalOpen(true)
    try {
      const changes = await getUncommittedChanges(projectId)
      setUncommittedChanges(changes)
    } catch {
      toast.error('Failed to fetch uncommitted changes')
      setUncommittedModalOpen(false)
    } finally {
      setUncommittedLoading(false)
    }
  }

  const handleRunChecks = async (obj: DeployableObject) => {
    if (!selectedProfile) {
      toast.error('Select a deployment profile first')
      return
    }
    const key = objKey(obj)
    setCheckRunning(true)
    setCheckTarget(key)
    setChecks(null)
    try {
      const result = await preDeployCheck(
        projectId, obj.obj_name, Number(selectedProfile), obj.obj_type
      )
      setChecks(result)
      if (result.all_passed) {
        toast.success('All pre-checks passed')
      } else {
        toast('Some checks did not pass', { icon: '\u26A0\uFE0F' })
      }
    } catch {
      toast.error('Pre-check failed')
    } finally {
      setCheckRunning(false)
    }
  }

  // Turn one deployment's source-control result into something to show. Only
  // the deploy that just ran carries git_status; history rows do not.
  const gitNoticeFor = (
    results: DeploymentHistoryItem[]
  ): { level: 'error' | 'warn' | 'ok'; text: string } | null => {
    const failed = results.filter((r) => r.git_status === 'failed')
    if (failed.length > 0) {
      const detail = failed[0].git_message || 'the remote rejected the push.'
      return {
        level: 'error',
        text:
          `Deployed to the app server, but the push to source control FAILED for ` +
          `${failed.map((r) => r.obj_name).join(', ')} — the repository does not ` +
          `have these changes. ${detail}`,
      }
    }
    const warned = results.filter((r) => r.git_status === 'warning')
    if (warned.length > 0) {
      return {
        level: 'warn',
        text: `Pushed to source control, but tagging did not complete: ${
          warned[0].git_message || 'unknown reason.'
        }`,
      }
    }
    const skipped = results.filter((r) => r.git_status === 'skipped')
    if (skipped.length > 0) {
      return { level: 'warn', text: skipped[0].git_message || 'Source control was skipped.' }
    }
    const tags = results.map((r) => r.git_tag).filter(Boolean)
    if (tags.length > 0) {
      return { level: 'ok', text: `Pushed and tagged ${tags.join(', ')}.` }
    }
    return null
  }

  // A fresh notice always starts collapsed.
  useEffect(() => {
    setGitNoticeExpanded(false)
  }, [gitNotice])

  // Only offer "Show more" when the message really overflows three lines.
  useEffect(() => {
    const el = gitNoticeRef.current
    if (!gitNotice || !el || gitNoticeExpanded) return
    setGitNoticeClamped(el.scrollHeight > el.clientHeight + 1)
  }, [gitNotice, gitNoticeExpanded])

  // Fetch origin and hold the deploy while the local project is behind. The
  // promise is settled by the modal, so callers just await a yes/no.
  const ensureRepoUpToDate = async (): Promise<boolean> => {
    let behind = 0
    let ahead: number | null = null
    setDeploying(true)
    setDeployProgress('Checking source control...')
    try {
      const res = await scmFetch(projectId)
      behind = Number(res?.behind) || 0
      ahead = res?.ahead ?? null
    } catch {
      // No repository, SCM off, or the remote is unreachable. A check that
      // could not run is not a reason to stop a deployment.
      return true
    } finally {
      setDeploying(false)
      setDeployProgress('')
    }
    if (behind <= 0) return true
    setSyncPrompt({ behind, ahead })
    return new Promise<boolean>((resolve) => {
      syncResolveRef.current = resolve
    })
  }

  const closeSyncPrompt = (proceed: boolean) => {
    const resolve = syncResolveRef.current
    syncResolveRef.current = null
    setSyncPrompt(null)
    resolve?.(proceed)
  }

  const pullThenDeploy = async () => {
    setSyncPulling(true)
    try {
      const res = await scmPull(projectId)
      if (res.conflict) {
        const n = res.conflicted_files?.length || 0
        toast.error(
          `The pull left conflicts in ${n} file(s). Resolve them in Source Control, ` +
          `then deploy again.`
        )
        closeSyncPrompt(false)
        return
      }
      toast.success('Pulled the latest changes')
      refreshObjects()
      closeSyncPrompt(true)
    } catch (err: unknown) {
      // Stay open so the pull can be retried or the deploy abandoned.
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Pull failed'
      toast.error(msg)
    } finally {
      setSyncPulling(false)
    }
  }

  const runDeploy = async (obj: DeployableObject, skipDb: boolean) => {
    setDeploying(true)
    setDeployProgress(`Deploying ${obj.obj_name}...`)
    try {
      const result = await executeDeploy({
        project_id: projectId,
        obj_name: obj.obj_name,
        profile_id: Number(selectedProfile),
        version_tag: versionTag || undefined,
        commit_message: commitMessage || undefined,
        obj_type: obj.obj_type,
        skip_db_structure: skipDb,
      })
      const notice = gitNoticeFor([result])
      setGitNotice(notice)
      if (result.status === 'completed') {
        if (notice?.level === 'error') {
          toast.error(`${obj.obj_name} deployed, but the push to source control failed`)
        } else {
          toast.success(`${obj.obj_name} deployed successfully`)
        }
      } else if (result.status === 'failed') {
        toast.error(`${obj.obj_name} failed: ${result.error_message || 'Unknown error'}`)
      }
      setChecks(null)
      setCheckTarget('')
      loadHistory()
      refreshObjects()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Deployment failed'
      toast.error(msg)
    } finally {
      setDeploying(false)
      setDeployProgress('')
    }
  }

  const handleDeploySingle = async (obj: DeployableObject) => {
    if (!selectedProfile) {
      toast.error('Select a deployment profile first')
      return
    }
    if (!(await ensureRepoUpToDate())) return
    // NAV objects don't have dynamic tables — deploy directly
    if (obj.obj_type === 'NAV') {
      await runDeploy(obj, false)
      return
    }
    // Fetch DB structure preview
    setPreviewLoading(true)
    try {
      const preview = await dbStructurePreview(
        projectId, obj.obj_name, Number(selectedProfile), obj.obj_type
      )
      if (!preview.has_changes) {
        // No DB changes — deploy directly
        await runDeploy(obj, false)
      } else {
        // Show preview modal
        setPreviewData(preview)
        setPendingDeploy({ mode: 'single', obj })
        setPreviewModalOpen(true)
      }
    } catch {
      // Preview failed — still allow deploy without preview
      toast('Could not fetch DB preview, deploying directly', { icon: '\u26A0\uFE0F' })
      await runDeploy(obj, false)
    } finally {
      setPreviewLoading(false)
    }
  }

  const runDeploySelected = async (
    selected: { obj_name: string; obj_type: string }[],
    skipDb: boolean,
  ) => {
    setDeploying(true)
    setDeployProgress(`Deploying ${selected.length} object(s)...`)
    try {
      const results = await deploySelected({
        project_id: projectId,
        profile_id: Number(selectedProfile),
        selected,
        version_tag: versionTag || undefined,
        commit_message: commitMessage || undefined,
        skip_db_structure: skipDb,
      })
      const succeeded = results.filter((r) => r.status === 'completed').length
      const failed = results.filter((r) => r.status === 'failed').length
      const notice = gitNoticeFor(results)
      setGitNotice(notice)
      if (failed === 0) {
        if (notice?.level === 'error') {
          toast.error(`${succeeded} deployed, but the push to source control failed`)
        } else {
          toast.success(`All ${succeeded} deployment(s) completed`)
        }
      } else {
        toast(`${succeeded} succeeded, ${failed} failed`, { icon: '\u26A0\uFE0F' })
      }
      setChecks(null)
      setCheckTarget('')
      loadHistory()
      refreshObjects()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Deploy failed'
      toast.error(msg)
    } finally {
      setDeploying(false)
      setDeployProgress('')
    }
  }

  const handleDeploySelected = async () => {
    if (!selectedProfile) {
      toast.error('Select a deployment profile first')
      return
    }
    if (selectedCount === 0) {
      toast.error('Select at least one object to deploy')
      return
    }
    if (!(await ensureRepoUpToDate())) return
    const selected = deployableObjects
      .filter((o) => selectedKeys.has(objKey(o)))
      .map((o) => ({ obj_name: o.obj_name, obj_type: o.obj_type }))

    if (!confirm(`Deploy ${selected.length} selected object(s)?`)) return

    // Collect previews for non-NAV objects
    const nonNavObjects = selected.filter((o) => o.obj_type !== 'NAV')
    if (nonNavObjects.length > 0) {
      setPreviewLoading(true)
      try {
        // Fetch previews for all non-NAV objects and combine
        const previews = await Promise.all(
          nonNavObjects.map((o) =>
            dbStructurePreview(projectId, o.obj_name, Number(selectedProfile), o.obj_type)
          )
        )
        const combinedTables: TableStructureChange[] = []
        let anyChanges = false
        for (const p of previews) {
          if (p.has_changes) anyChanges = true
          combinedTables.push(...p.tables)
        }
        if (anyChanges) {
          setPreviewData({ tables: combinedTables, has_changes: true })
          setPendingDeploy({ mode: 'selected', selected })
          setPreviewModalOpen(true)
          setPreviewLoading(false)
          return
        }
      } catch {
        toast('Could not fetch DB preview, deploying directly', { icon: '\u26A0\uFE0F' })
      } finally {
        setPreviewLoading(false)
      }
    }

    // No changes or preview failed — deploy directly
    await runDeploySelected(selected, false)
  }

  const handleRollback = async (deploymentId: number) => {
    if (!confirm('Are you sure you want to rollback this deployment?')) return
    try {
      await rollbackDeployment(deploymentId)
      toast.success('Deployment rolled back')
      loadHistory()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Rollback failed'
      toast.error(msg)
    }
  }

  const handlePreviewAction = async (skipDb: boolean) => {
    setPreviewModalOpen(false)
    if (!pendingDeploy) return
    if (pendingDeploy.mode === 'single' && pendingDeploy.obj) {
      await runDeploy(pendingDeploy.obj, skipDb)
    } else if (pendingDeploy.mode === 'selected' && pendingDeploy.selected) {
      await runDeploySelected(pendingDeploy.selected, skipDb)
    }
    setPendingDeploy(null)
    setPreviewData(null)
  }

  const closePreviewModal = () => {
    setPreviewModalOpen(false)
    setPendingDeploy(null)
    setPreviewData(null)
  }

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '-'
    if (seconds < 60) return `${seconds}s`
    return `${Math.floor(seconds / 60)}m ${seconds % 60}s`
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleString()
  }

  return (
    <div style={{ padding: '0 32px 24px', maxWidth: 1200 }} className="deploy-page">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
        <button
          onClick={() => navigate('/workbench')}
          className="btn btn-ghost btn-sm"
        >
          &larr; Back
        </button>
        <h1 style={{ fontSize: 23, fontWeight: 600, margin: 0 }}>
          Deploy to Proteus Vision ERP: {projectTitle}
        </h1>
      </div>

      {/* Deploy Settings Row */}
      <div className="card" style={{ padding: 20, marginBottom: 24 }}>
        <div className="deploy-settings-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <label className="form-label">Deployment Profile</label>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                style={{ padding: '0 6px', fontSize: 13 }}
                onClick={() => setShowProfiles((v) => !v)}
                title="Create, edit and activate deployment targets"
              >
                {showProfiles ? 'Hide targets' : 'Manage targets'}
              </button>
            </div>
            <select
              className="form-input"
              value={selectedProfile}
              onChange={(e) => {
                setSelectedProfile(e.target.value ? Number(e.target.value) : '')
                setChecks(null)
                setCheckTarget('')
              }}
            >
              <option value="">Select profile...</option>
              {profiles.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.profile_name} ({p.application_database})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">Version Tag</label>
            <input
              className="form-input"
              value={versionTag}
              onChange={(e) => setVersionTag(e.target.value)}
              placeholder="Auto (next version per object)"
            />
            <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4 }}>
              Leave blank to tag each changed object with its own next version.
            </div>
          </div>
          <div>
            <label className="form-label">Commit Message</label>
            <input
              className="form-input"
              value={commitMessage}
              onChange={(e) => setCommitMessage(e.target.value)}
              placeholder="Deploy objects"
            />
          </div>
        </div>

        {/* Deployment TARGETS live here rather than in user Settings: they are
            an advanced, admin-level concern that belongs next to the deploy
            that consumes them, not beside everyday preferences. */}
        {showProfiles && (
          <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid var(--color-border)' }}>
            <DeploymentProfilesPanel onChanged={loadProfiles} />
          </div>
        )}
      </div>

      {/* Source-control outcome of the last deploy. Separate from the
          deployment status on purpose: the app server can be updated while the
          repository is not, and that combination used to be invisible. */}
      {gitNotice && (
        <div
          className="card"
          style={{
            padding: '12px 16px',
            marginBottom: 24,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            borderLeft: `3px solid ${
              gitNotice.level === 'error'
                ? 'var(--color-danger, #e5484d)'
                : gitNotice.level === 'warn'
                  ? 'var(--color-warning, #f5a524)'
                  : 'var(--color-success, #30a46c)'
            }`,
          }}
        >
          <span style={{ fontSize: 15 }}>
            {gitNotice.level === 'error' ? '\u26D4' : gitNotice.level === 'warn' ? '\u26A0\uFE0F' : '\u2713'}
          </span>
          <div style={{ flex: 1, minWidth: 0, fontSize: 14 }}>
            <div
              ref={gitNoticeRef}
              onClick={() => gitNoticeClamped && setGitNoticeExpanded((v) => !v)}
              title={gitNoticeClamped ? (gitNoticeExpanded ? 'Click to collapse' : 'Click to show the full message') : undefined}
              style={{
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                cursor: gitNoticeClamped ? 'pointer' : 'default',
                ...(gitNoticeExpanded
                  ? {}
                  : {
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical' as const,
                      overflow: 'hidden',
                    }),
              }}
            >
              {gitNotice.text}
            </div>
            {gitNoticeClamped && (
              <button
                type="button"
                onClick={() => setGitNoticeExpanded((v) => !v)}
                style={{
                  marginTop: 4,
                  padding: 0,
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  fontSize: 13,
                  color: 'var(--color-primary, #3b82f6)',
                }}
              >
                {gitNoticeExpanded ? 'Show less' : 'Show more'}
              </button>
            )}
          </div>
          <button
            className="btn btn-ghost btn-sm"
            style={{ padding: '0 6px' }}
            onClick={() => setGitNotice(null)}
            title="Dismiss"
          >
            &times;
          </button>
        </div>
      )}

      {/* Objects Table */}
      <div className="card" style={{ padding: 24, marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
          <h2 style={{ fontSize: 17, fontWeight: 600, margin: 0 }}>
            Deployable Objects
            {changedObjects.length > 0 && (
              <span style={{ fontSize: 14, fontWeight: 400, color: 'var(--color-text-secondary)', marginLeft: 8 }}>
                ({changedObjects.length} changed)
              </span>
            )}
          </h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {changedObjects.length > 0 && (
              <button
                className="btn btn-ghost btn-sm"
                onClick={selectAllChanged}
              >
                Select All Changed
              </button>
            )}
            <button
              className="btn btn-accent btn-sm"
              onClick={handleDeploySelected}
              disabled={deploying || selectedCount === 0 || !selectedProfile}
            >
              {deploying ? 'Deploying...' : `Deploy Selected (${selectedCount})`}
            </button>
          </div>
        </div>

        {/* Progress bar */}
        {deploying && deployProgress && (
          <div
            style={{
              padding: 10,
              borderRadius: 8,
              marginBottom: 12,
              background: 'var(--color-info-bg, rgba(59,130,246,0.1))',
              border: '1px solid rgba(59,130,246,0.3)',
              fontSize: 14,
            }}
          >
            <span className="spinner" style={{ marginRight: 8 }} />
            {deployProgress}
          </div>
        )}

        {/* Pre-check results */}
        {checks && checkTarget && (
          <div
            style={{
              padding: 14,
              borderRadius: 8,
              marginBottom: 12,
              background: checks.all_passed
                ? 'var(--color-success-bg, rgba(34,197,94,0.1))'
                : 'var(--color-warning-bg, rgba(249,115,22,0.1))',
              border: `1px solid ${checks.all_passed ? 'rgba(34,197,94,0.3)' : 'rgba(249,115,22,0.3)'}`,
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
              Pre-check: {checkTarget.split(':')[1]}
            </div>
            <div className="deploy-checks-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, fontSize: 13 }}>
              <div>{checks.profile_valid ? '\u2705' : '\u274C'} Profile valid</div>
              <div>
                {checks.model_found ? '\u2705' : '\u274C'} Model found
                {checks.model_message ? ` - ${checks.model_message}` : ''}
              </div>
              <div>
                {checks.scm_status === 'clean' || checks.scm_status === 'disabled' ? '\u2705' : '\u274C'}{' '}
                SCM: {checks.scm_message}
                {checks.scm_status === 'unpushed' && (
                  <button
                    onClick={handleViewUncommittedChanges}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--color-accent)',
                      cursor: 'pointer',
                      fontSize: 13,
                      textDecoration: 'underline',
                      marginLeft: 6,
                      padding: 0,
                    }}
                  >
                    View Changes
                  </button>
                )}
              </div>
              <div>
                {checks.db_connectable ? '\u2705' : '\u274C'} DB connection
                {checks.db_message ? ` - ${checks.db_message}` : ''}
              </div>
              <div>{checks.app_server_configured ? '\u2705' : '\u26A0\uFE0F'} App server</div>
            </div>

            {/* DB Structure Preview */}
            {checks.db_structure && (
              <div style={{ marginTop: 12, borderTop: '1px solid rgba(128,128,128,0.2)', paddingTop: 12 }}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                  {checks.db_structure.has_changes
                    ? '\u26A0\uFE0F DB Structure Changes (will be applied on deploy)'
                    : '\u2705 DB Structure — No changes needed'}
                </div>
                {checks.db_structure.tables.map((table) => (
                  <div key={table.table_name} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 600 }}>
                        {table.table_name}
                      </span>
                      {table.status === 'new' && (
                        <span style={{
                          padding: '1px 6px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                          color: '#fff', background: '#3b82f6',
                        }}>New Table</span>
                      )}
                      {table.status === 'modified' && (
                        <span style={{
                          padding: '1px 6px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                          color: '#fff', background: '#f97316',
                        }}>Modified</span>
                      )}
                      {table.status === 'unchanged' && (
                        <span style={{
                          padding: '1px 6px', borderRadius: 8, fontSize: 13, fontWeight: 500,
                          color: 'var(--color-text-secondary)',
                          background: 'var(--color-bg-secondary, rgba(128,128,128,0.1))',
                        }}>Unchanged</span>
                      )}
                    </div>
                    {(table.columns_to_add.length > 0 || table.columns_to_modify.length > 0 || table.columns_not_in_design.length > 0 || table.pk_action) && (
                      <div style={{
                        background: 'var(--color-bg-secondary, rgba(128,128,128,0.06))',
                        borderRadius: 4, padding: '6px 10px', fontFamily: 'monospace',
                        fontSize: 13, lineHeight: 1.7,
                      }}>
                        {table.columns_to_add.map((col) => (
                          <div key={col.name} style={{ color: '#22c55e' }}>
                            + {col.name}&nbsp;&nbsp;{col.type}
                            {col.is_pk && <span style={{ color: '#a855f7', marginLeft: 6 }}>PK</span>}
                          </div>
                        ))}
                        {table.columns_to_modify.map((col) => (
                          <div key={col.name} style={{ color: '#f59e0b' }}>
                            ~ {col.name}&nbsp;&nbsp;{col.old_type} &rarr; {col.new_type}
                          </div>
                        ))}
                        {table.columns_not_in_design.map((col) => (
                          <div key={col.name} style={{ color: 'var(--color-text-secondary)' }}>
                            - {col.name}&nbsp;&nbsp;{col.type}
                            <span style={{ fontSize: 13, opacity: 0.7 }}> (will not be dropped)</span>
                          </div>
                        ))}
                        {table.pk_action === 'create' && table.primary_key && table.primary_key.length > 0 && (
                          <div style={{ color: '#a855f7', marginTop: 2 }}>
                            + PRIMARY KEY ({table.primary_key.join(', ')})
                          </div>
                        )}
                        {table.pk_action === 'differs' && (
                          <div style={{ color: '#f59e0b', marginTop: 2 }}>
                            ~ PRIMARY KEY: DB has ({(table.existing_primary_key || []).join(', ')}) — design has ({(table.primary_key || []).join(', ')})
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {loadingObjects ? (
          <div style={{ textAlign: 'center', padding: 24, color: 'var(--color-text-secondary)' }}>
            Loading objects...
          </div>
        ) : deployableObjects.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 24, color: 'var(--color-text-secondary)' }}>
            No deployable objects found
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: 700, borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--color-border)' }}>
                  <th style={{ textAlign: 'center', padding: '8px 8px', width: 36 }}>
                    <input
                      type="checkbox"
                      checked={selectedKeys.size === deployableObjects.length && deployableObjects.length > 0}
                      onChange={toggleSelectAll}
                      style={{ cursor: 'pointer' }}
                    />
                  </th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600 }}>Object Name</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600 }}>Type</th>
                  <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 600 }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600 }}>Last Deployed</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600 }}>Last Version</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 600 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {deployableObjects.map((obj) => {
                  const key = objKey(obj)
                  const isSelected = selectedKeys.has(key)
                  const isChanged = obj.has_changed || obj.never_deployed
                  return (
                    <tr
                      key={key}
                      style={{
                        borderBottom: '1px solid var(--color-border)',
                        background: isSelected ? 'var(--color-bg-hover, rgba(59,130,246,0.04))' : undefined,
                      }}
                    >
                      <td style={{ textAlign: 'center', padding: '8px 8px' }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(key)}
                          style={{ cursor: 'pointer' }}
                        />
                      </td>
                      <td style={{ padding: '8px 12px', fontWeight: isChanged ? 600 : 400 }}>
                        {obj.obj_name}
                        {obj.case_twins && obj.case_twins.length > 0 && (
                          <div style={{ fontSize: 12, fontWeight: 400, color: obj.in_menu ? 'var(--color-text-secondary)' : '#dc2626' }}>
                            {obj.in_menu
                              ? `Also present as ${obj.case_twins.join(', ')} (different letter case) — this is the one in the menu`
                              : `Duplicate of ${obj.case_twins.join(', ')} with different letter case, not in the menu — likely a leftover; remove it from the project`}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                          {obj.obj_type_label}
                        </span>
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                        <ChangeStatusBadge obj={obj} />
                      </td>
                      <td style={{ padding: '8px 12px', fontSize: 13 }}>
                        {obj.last_deployed_at ? formatDate(obj.last_deployed_at) : '-'}
                      </td>
                      <td style={{ padding: '8px 12px', fontSize: 13, fontFamily: 'monospace' }}>
                        {obj.last_version || '-'}
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {obj.obj_type === 'SITE' && user && (
                          <button
                            className="btn btn-ghost btn-sm"
                            style={{ marginRight: 4 }}
                            title="Open website preview in new tab"
                            onClick={() => {
                              const url = `/site/${encodeURIComponent(user.user_id)}/${encodeURIComponent(projectId)}/${encodeURIComponent(obj.obj_name)}`
                              window.open(url, '_blank', 'noopener')
                            }}
                          >
                            Open ↗
                          </button>
                        )}
                        {obj.obj_type !== 'SITE' && (
                          <button
                            className="btn btn-ghost btn-sm"
                            style={{ marginRight: 4 }}
                            onClick={() => handleRunChecks(obj)}
                            disabled={checkRunning || !selectedProfile}
                          >
                            {checkRunning && checkTarget === key ? 'Checking...' : 'Pre-check'}
                          </button>
                        )}
                        <button
                          className={isChanged ? 'btn btn-accent btn-sm' : 'btn btn-ghost btn-sm'}
                          onClick={() => handleDeploySingle(obj)}
                          disabled={deploying || !selectedProfile}
                        >
                          Deploy
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Preview loading overlay */}
      {previewLoading && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999,
          }}
        >
          <div
            className="card"
            style={{ padding: 24, textAlign: 'center', minWidth: 260 }}
          >
            <span className="spinner" style={{ marginRight: 8 }} />
            Analyzing DB structure changes...
          </div>
        </div>
      )}

      {/* DB Structure Preview Modal */}
      {previewModalOpen && previewData && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={closePreviewModal}
        >
          <div
            className="card"
            style={{
              width: '90%',
              maxWidth: 720,
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              padding: 0,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              style={{
                padding: '16px 24px',
                borderBottom: '1px solid var(--color-border)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 600 }}>
                Database Structure Changes
              </h3>
              <button
                onClick={closePreviewModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 19,
                  cursor: 'pointer',
                  color: 'var(--color-text-secondary)',
                  padding: '2px 6px',
                }}
              >
                &times;
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '16px 24px', overflowY: 'auto', flex: 1 }}>
              {previewData.tables
                .filter((t) => t.status !== 'unchanged')
                .map((table) => (
                  <div key={table.table_name} style={{ marginBottom: 20 }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        marginBottom: 8,
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 600,
                          fontSize: 15,
                          fontFamily: 'monospace',
                        }}
                      >
                        {table.table_name}
                      </span>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '1px 8px',
                          borderRadius: 10,
                          fontSize: 13,
                          fontWeight: 600,
                          color: '#fff',
                          background:
                            table.status === 'new' ? '#3b82f6' : '#f97316',
                        }}
                      >
                        {table.status === 'new' ? 'New' : 'Modified'}
                      </span>
                    </div>

                    <div
                      style={{
                        background: 'var(--color-bg-secondary, rgba(128,128,128,0.06))',
                        borderRadius: 6,
                        padding: '8px 12px',
                        fontFamily: 'monospace',
                        fontSize: 13,
                        lineHeight: 1.8,
                      }}
                    >
                      {table.columns_to_add.map((col) => (
                        <div key={col.name} style={{ color: '#22c55e' }}>
                          + {col.name}&nbsp;&nbsp;{col.type}
                          {col.is_pk && <span style={{ color: '#a855f7', marginLeft: 6 }}>PK</span>}
                        </div>
                      ))}
                      {table.columns_to_modify.map((col) => (
                        <div key={col.name} style={{ color: '#f59e0b' }}>
                          ~ {col.name}&nbsp;&nbsp;{col.old_type} &rarr;{' '}
                          {col.new_type}
                        </div>
                      ))}
                      {table.columns_not_in_design.map((col) => (
                        <div
                          key={col.name}
                          style={{ color: 'var(--color-text-secondary)' }}
                        >
                          - {col.name}&nbsp;&nbsp;{col.type}{' '}
                          <span style={{ fontSize: 13, opacity: 0.7 }}>
                            (will not be dropped)
                          </span>
                        </div>
                      ))}
                      {table.pk_action === 'create' && table.primary_key && table.primary_key.length > 0 && (
                        <div style={{ color: '#a855f7', marginTop: 2 }}>
                          + PRIMARY KEY ({table.primary_key.join(', ')})
                        </div>
                      )}
                      {table.pk_action === 'differs' && (
                        <div style={{ color: '#f59e0b', marginTop: 2 }}>
                          ~ PRIMARY KEY: DB has ({(table.existing_primary_key || []).join(', ')}) — design has ({(table.primary_key || []).join(', ')})
                        </div>
                      )}
                      {table.columns_to_add.length === 0 &&
                        table.columns_to_modify.length === 0 &&
                        table.columns_not_in_design.length === 0 &&
                        !table.pk_action && (
                          <div style={{ color: 'var(--color-text-secondary)' }}>
                            No column changes
                          </div>
                        )}
                    </div>
                  </div>
                ))}

              {/* Show unchanged tables summary */}
              {previewData.tables.filter((t) => t.status === 'unchanged').length >
                0 && (
                <div
                  style={{
                    fontSize: 13,
                    color: 'var(--color-text-secondary)',
                    marginTop: 8,
                  }}
                >
                  {
                    previewData.tables.filter((t) => t.status === 'unchanged')
                      .length
                  }{' '}
                  table(s) unchanged
                </div>
              )}
            </div>

            {/* Footer */}
            <div
              style={{
                padding: '12px 24px',
                borderTop: '1px solid var(--color-border)',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
              }}
            >
              <button
                className="btn btn-ghost"
                onClick={closePreviewModal}
              >
                Cancel
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => handlePreviewAction(true)}
              >
                Skip DB Changes & Deploy
              </button>
              <button
                className="btn btn-primary"
                onClick={() => handlePreviewAction(false)}
              >
                Apply Changes & Deploy
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Behind-the-remote gate. A deploy commits and pushes the project, so it
          is held until the remote commits are in the local copy. */}
      {syncPrompt && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => !syncPulling && closeSyncPrompt(false)}
        >
          <div
            className="card"
            style={{ width: '90%', maxWidth: 520, padding: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: '16px 24px',
                borderBottom: '1px solid var(--color-border)',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <span style={{ fontSize: 17 }}>&#9888;&#65039;</span>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 600 }}>
                Source control has newer changes
              </h3>
            </div>

            <div style={{ padding: '16px 24px', fontSize: 14, lineHeight: 1.6 }}>
              <p style={{ margin: '0 0 10px' }}>
                The remote repository has{' '}
                <strong>
                  {syncPrompt.behind} commit{syncPrompt.behind !== 1 ? 's' : ''}
                </strong>{' '}
                that this project does not have. Deploying also commits and pushes these
                files, which would push your older copies over that work.
              </p>
              <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
                Pull the latest changes before deploying.
                {syncPrompt.ahead ? (
                  <> Your {syncPrompt.ahead} local commit
                    {syncPrompt.ahead !== 1 ? 's are' : ' is'} kept — the pull merges,
                    it does not discard them.</>
                ) : null}
              </p>
            </div>

            <div
              style={{
                padding: '12px 24px',
                borderTop: '1px solid var(--color-border)',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
              }}
            >
              <button
                className="btn btn-ghost"
                disabled={syncPulling}
                onClick={() => closeSyncPrompt(false)}
              >
                Cancel deployment
              </button>
              <button
                className="btn btn-primary"
                disabled={syncPulling}
                onClick={pullThenDeploy}
              >
                {syncPulling ? (
                  <>
                    <span className="spinner" style={{ marginRight: 6 }} />
                    Pulling...
                  </>
                ) : (
                  'Pull latest and deploy'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Uncommitted Changes Modal */}
      {uncommittedModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setUncommittedModalOpen(false)}
        >
          <div
            className="card"
            style={{
              width: '90%',
              maxWidth: 800,
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
              padding: 0,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              style={{
                padding: '16px 24px',
                borderBottom: '1px solid var(--color-border)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 600 }}>
                Uncommitted Changes
                {!uncommittedLoading && uncommittedChanges.length > 0 && (
                  <span style={{ fontSize: 14, fontWeight: 400, color: 'var(--color-text-secondary)', marginLeft: 8 }}>
                    ({uncommittedChanges.length} file{uncommittedChanges.length !== 1 ? 's' : ''})
                  </span>
                )}
              </h3>
              <button
                onClick={() => setUncommittedModalOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 19,
                  cursor: 'pointer',
                  color: 'var(--color-text-secondary)',
                  padding: '2px 6px',
                }}
              >
                &times;
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '16px 24px', overflowY: 'auto', flex: 1 }}>
              {uncommittedLoading ? (
                <div style={{ textAlign: 'center', padding: 24, color: 'var(--color-text-secondary)' }}>
                  <span className="spinner" style={{ marginRight: 8 }} />
                  Loading changes...
                </div>
              ) : uncommittedChanges.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 24, color: 'var(--color-text-secondary)' }}>
                  No uncommitted changes found
                </div>
              ) : (
                uncommittedChanges.map((change, i) => {
                  const statusColors: Record<string, string> = {
                    modified: '#f59e0b',
                    added: '#22c55e',
                    deleted: '#ef4444',
                    untracked: '#3b82f6',
                    renamed: '#8b5cf6',
                    copied: '#06b6d4',
                  }
                  const statusColor = statusColors[change.status] || '#6b7280'
                  return (
                    <div key={i} style={{ marginBottom: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '1px 8px',
                            borderRadius: 10,
                            fontSize: 13,
                            fontWeight: 600,
                            color: '#fff',
                            background: statusColor,
                            textTransform: 'uppercase',
                          }}
                        >
                          {change.status}
                        </span>
                        <span style={{ fontFamily: 'monospace', fontSize: 14 }}>
                          {change.file_path}
                        </span>
                      </div>
                      {change.diff && (
                        <pre
                          style={{
                            background: 'var(--color-bg-secondary, rgba(128,128,128,0.06))',
                            borderRadius: 6,
                            padding: '10px 14px',
                            fontFamily: 'monospace',
                            fontSize: 13,
                            lineHeight: 1.6,
                            overflow: 'auto',
                            maxHeight: 300,
                            margin: 0,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                          }}
                        >
                          {change.diff.split('\n').map((line, j) => {
                            let color = 'inherit'
                            if (line.startsWith('+') && !line.startsWith('+++')) color = '#22c55e'
                            else if (line.startsWith('-') && !line.startsWith('---')) color = '#ef4444'
                            else if (line.startsWith('@@')) color = '#3b82f6'
                            return (
                              <div key={j} style={{ color }}>
                                {line}
                              </div>
                            )
                          })}
                        </pre>
                      )}
                    </div>
                  )
                })
              )}
            </div>

            {/* Footer */}
            <div
              style={{
                padding: '12px 24px',
                borderTop: '1px solid var(--color-border)',
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <button
                className="btn btn-ghost"
                onClick={() => setUncommittedModalOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deployment History */}
      <div className="card" style={{ padding: 24 }}>
        <h2 style={{ fontSize: 17, fontWeight: 600, marginBottom: 16 }}>
          Deployment History
        </h2>

        {loadingHistory ? (
          <div style={{ textAlign: 'center', padding: 24, color: 'var(--color-text-secondary)' }}>
            Loading...
          </div>
        ) : history.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 24, color: 'var(--color-text-secondary)' }}>
            No deployments yet
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: 780, borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600 }}>Version</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600 }}>Object</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600 }}>Type</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600 }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600 }}>Started</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600 }}>Duration</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600 }}>Git</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 600 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {history.map((d) => (
                  <tr
                    key={d.id}
                    style={{
                      borderBottom: '1px solid var(--color-border)',
                      cursor: 'pointer',
                    }}
                    onClick={() => navigate(`/deploy/${encodeURIComponent(projectId)}/history/${d.id}`)}
                  >
                    <td style={{ padding: '8px 12px' }}>{d.version_tag || '-'}</td>
                    <td style={{ padding: '8px 12px' }}>{d.obj_name}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                        {d.obj_type_label || d.obj_type || 'Transaction'}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <StatusBadge status={d.status} />
                    </td>
                    <td style={{ padding: '8px 12px' }}>{formatDate(d.started_at)}</td>
                    <td style={{ padding: '8px 12px' }}>{formatDuration(d.duration_seconds)}</td>
                    <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 13 }}>
                      {d.git_commit_hash ? d.git_commit_hash.substring(0, 7) : '-'}
                    </td>
                    <td
                      style={{ padding: '8px 12px', textAlign: 'right' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {d.can_rollback && d.status === 'completed' && (
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleRollback(d.id)}
                        >
                          Rollback
                        </button>
                      )}
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ marginLeft: 4 }}
                        onClick={() =>
                          navigate(`/deploy/${encodeURIComponent(projectId)}/history/${d.id}`)
                        }
                      >
                        Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
